import { MainSmsIntegrationTypeEnum } from '@zaoblako/shared/modules/integration/enums/main.sms.integration.type.enum';
import { MainSmsIntegrationTypeEnumI18n } from '@zaoblako/shared/modules/integration/i18n/main-sms-integration-type-enum.i18n';
import { SerdeDtoClass } from '@zaoblako/shared/modules/serde/classes/serde-dto.class';
import { IntergrationDtoKeyEnum } from '@zaoblako/shared/modules/integration/enums/intergration-dto-key.enum';
import { DtoMessageEnum } from '@zaoblako/shared/common/enums/dto.message.enum';
import { commonEnumValidator } from '@zaoblako/shared/modules/serde/validators/common-enum.validator';
import type { SerdeContextDataInterface } from '@zaoblako/shared/modules/serde/interfaces/serde-context-data.interface';
import type { SerdeContextInterface } from '@zaoblako/shared/modules/serde/interfaces/serde-context.interface';

import * as yup from 'yup';

/** MainSMS account create and update APIs request body - DTO signature */
export interface MainSmsAccountFormInterface {
  /** Integration type */
  type: MainSmsIntegrationTypeEnum;
  /** Sender name */
  sender: string;
  /** SMS-sender API key (required only when {@link MainSmsAccountFormInterface.type} equals {@link MainSmsIntegrationTypeEnum.SMS}) */
  apiKey?: string;
  /** Project name (required only when {@link MainSmsAccountFormInterface.type} equals {@link MainSmsIntegrationTypeEnum.SMS}) */
  project?: string;
  /** Email-sender API key (required only when {@link MainSmsAccountFormInterface.type} equals {@link MainSmsIntegrationTypeEnum.EMAIL}) */
  mailingApiKey?: string;
  /** Verified email addresses list (required only when {@link MainSmsAccountFormInterface.type} equals {@link MainSmsIntegrationTypeEnum.EMAIL}) */
  emails?: string[];
}

const requiredOnType = (type: MainSmsIntegrationTypeEnum) => (
  context: SerdeContextInterface<SerdeContextDataInterface, MainSmsAccountFormInterface>,
): boolean => context?.value?.type !== type;

const hintOnType = (type: MainSmsIntegrationTypeEnum): string => `Значение обязательно только если поле \`type\` равно \`"${type}"\``;

/** MainSMS account create and update APIs request body - DTO schema */
export const MainSmsAccountFormDto = SerdeDtoClass.create<MainSmsAccountFormInterface>(IntergrationDtoKeyEnum.MAIN_SMS_ACCOUNT_FORM_DTO, {
  type: {
    label: 'Тип интеграции',
    serializer: commonEnumValidator(MainSmsIntegrationTypeEnum, MainSmsIntegrationTypeEnumI18n),
  },
  sender: {
    label: 'Имя отправителя',
    serializer: yup
      .string()
      .min(1, DtoMessageEnum.EMPTY_STRING),
  },
  apiKey: {
    optional: requiredOnType(MainSmsIntegrationTypeEnum.SMS),
    label: 'Ключ авторизации в API',
    hint: hintOnType(MainSmsIntegrationTypeEnum.SMS),
    serializer: (context: SerdeContextInterface<SerdeContextDataInterface, MainSmsAccountFormInterface>) => {
      const baseSchema = yup.string();
      return context?.value?.type === MainSmsIntegrationTypeEnum.SMS
        ? baseSchema.min(1, DtoMessageEnum.EMPTY_STRING)
        : baseSchema;
    },
  },
  project: {
    optional: requiredOnType(MainSmsIntegrationTypeEnum.SMS),
    label: 'Проект',
    hint: hintOnType(MainSmsIntegrationTypeEnum.SMS),
    serializer: (context: SerdeContextInterface<SerdeContextDataInterface, MainSmsAccountFormInterface>) => {
      const baseSchema = yup.string();
      return context?.value?.type === MainSmsIntegrationTypeEnum.SMS
        ? baseSchema.min(1, DtoMessageEnum.EMPTY_STRING)
        : baseSchema;
    },
  },
  mailingApiKey: {
    optional: requiredOnType(MainSmsIntegrationTypeEnum.EMAIL),
    label: 'Ключ авторизации в API',
    hint: hintOnType(MainSmsIntegrationTypeEnum.EMAIL),
    serializer: (context: SerdeContextInterface<SerdeContextDataInterface, MainSmsAccountFormInterface>) => {
      const baseSchema = yup.string();
      return context?.value?.type === MainSmsIntegrationTypeEnum.EMAIL
        ? baseSchema.min(1, DtoMessageEnum.EMPTY_STRING)
        : baseSchema;
    },
  },
  emails: {
    optional: requiredOnType(MainSmsIntegrationTypeEnum.EMAIL),
    label: 'Верифицированные email-адреса',
    hint: hintOnType(MainSmsIntegrationTypeEnum.EMAIL),
    serializer: (context: SerdeContextInterface<SerdeContextDataInterface, MainSmsAccountFormInterface>) => {
      const baseSchema = yup.array(
        yup
          .string()
          .email()
      );

      return context?.value?.type === MainSmsIntegrationTypeEnum.EMAIL
        ? baseSchema.min(1, DtoMessageEnum.EMPTY_ARRAY)
        : baseSchema;
    },
  },
});
